/**
 * Frontend mirror of helpers/billPayers.helper.js — keep both in sync.
 *
 * Pure rules for splitting a bill across payer accounts (SHOW_BILL_PAYERS).
 *
 * A bill's `payers` is a list of { account, percentage }. The percentages may
 * not exceed 100 in total; whatever is left over is the asset owner's share.
 * Bills with no payers keep the legacy partyType rule (see billOwnerAmount),
 * so balances of existing data never change.
 */

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const accountKey = (account) => String(account?._id || account || '');

/** Accepts raw request input; returns [] for anything empty. */
export const normalizePayers = (payers) => {
	if (!Array.isArray(payers)) return [];
	return payers
		.filter((p) => p && (p.account || p.percentage !== undefined))
		.map((p) => ({ account: p.account?._id || p.account, percentage: Number(p.percentage) }));
};

export const payersTotal = (payers = []) =>
	round2(normalizePayers(payers).reduce((sum, p) => sum + (Number.isFinite(p.percentage) ? p.percentage : 0), 0));

/**
 * Returns a list of human-readable (Spanish, user-facing) errors; empty when valid.
 * `ownerAccount` (optional) rejects charging the bill's own account as a payer.
 */
export const validatePayers = (payers, { ownerAccount } = {}) => {
	const list = normalizePayers(payers);
	const errors = [];
	const seen = new Set();
	list.forEach((p, i) => {
		const n = i + 1;
		if (!p.account) errors.push(`Pagador ${n}: seleccione una cuenta.`);
		if (!Number.isFinite(p.percentage) || p.percentage <= 0) {
			errors.push(`Pagador ${n}: el porcentaje debe ser mayor a 0.`);
		} else if (p.percentage > 100) {
			errors.push(`Pagador ${n}: el porcentaje no puede superar 100.`);
		}
		const key = accountKey(p.account);
		if (key && seen.has(key)) errors.push(`Pagador ${n}: la cuenta está repetida.`);
		if (key) seen.add(key);
		if (key && ownerAccount && key === accountKey(ownerAccount)) {
			errors.push(`Pagador ${n}: la cuenta del gasto no puede ser también pagadora.`);
		}
	});
	if (payersTotal(list) > 100) {
		errors.push(`La suma de porcentajes (${payersTotal(list)}%) no puede superar 100%.`);
	}
	return errors;
};

export const hasPayers = (bill) => Array.isArray(bill?.payers) && bill.payers.length > 0;

/** Amount a single payer owes for this bill, rounded to cents. */
export const payerShareAmount = (bill, payer) => round2(((Number(bill?.amount) || 0) * (Number(payer?.percentage) || 0)) / 100);

/**
 * Portion of the bill that stays with the asset owner.
 * - With payers: amount minus every payer's rounded share (so the parts always
 *   add back up to the bill amount exactly).
 * - Without payers: legacy rule — tenant ('inquilino') bills charge the owner
 *   nothing, every other bill charges the owner in full.
 */
export const billOwnerAmount = (bill) => {
	const amount = Number(bill?.amount) || 0;
	if (hasPayers(bill)) {
		const paidByOthers = bill.payers.reduce((sum, p) => sum + payerShareAmount(bill, p), 0);
		return round2(Math.max(0, amount - paidByOthers));
	}
	return bill?.partyType === 'inquilino' ? 0 : amount;
};
